import { useState, type FormEvent } from "react";
import SiteBreadcrumbs from "@/components/feature/Breadcrumbs";
import { siteInfo } from "@/mocks/site";
import { useSeo } from "@/hooks/useSeo";

const FORM_URL = "https://readdy.ai/api/form/dahi0kboh653ivfvo020";

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

const contactChannels = [
  {
    icon: "ri-mail-line",
    title: "读者来信与纠错",
    value: siteInfo.email,
    desc: "文章内容有误、想补充线索，都可以发到这里。",
  },
  {
    icon: "ri-briefcase-line",
    title: "内容合作",
    value: siteInfo.editorEmail,
    desc: "选题共创、栏目共建等合作，欢迎邮件沟通。",
  },
  {
    icon: "ri-time-line",
    title: "回复时间",
    value: "工作日 10:00 - 19:00",
    desc: "留言一般当天处理，线索类我们会单独核实。",
  },
];

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  useSeo({
    title: "联系我们 - 留言纠错与内容合作 | 瓜田秘闻社",
    description:
      "想给瓜田秘闻社投稿线索、纠正内容错误或洽谈合作？在这里留下你的联系方式与留言，我们在工作日内会尽快回应。",
    keywords: "黑料吃瓜,联系瓜田秘闻社,投稿线索,内容纠错",
    canonical: "/contact",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: "联系我们",
      inLanguage: "zh-CN",
      about: { "@type": "Organization", name: siteInfo.name, email: siteInfo.email },
    },
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const guard = String(formData.get("company_alt") ?? "").trim();
    if (guard) {
      setStatus("success");
      return;
    }
    setStatus("submitting");
    setFormError("");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "company_alt") return;
      if (typeof value === "string" && value.trim() === "") return;
      payload.append(key, value);
    });

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，暂时没能提交，请稍后再试。");
    }
  };

  return (
    <div className="w-full">
      <div className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
          <SiteBreadcrumbs items={[{ label: "首页", to: "/" }, { label: "联系我们" }]} />
          <h1 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
            联系我们
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
            纠错、投稿、合作，或者只是想聊聊今天最大的瓜，都欢迎给我们留言。
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <aside className="flex flex-col gap-4 lg:col-span-4">
            {contactChannels.map((channel) => (
              <div key={channel.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                  <i className={`${channel.icon} text-lg`}></i>
                </span>
                <h2 className="mt-3 font-heading text-base font-bold text-foreground-950">
                  {channel.title}
                </h2>
                <p className="mt-1 text-sm font-medium text-primary-700">{channel.value}</p>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{channel.desc}</p>
              </div>
            ))}
          </aside>

          <div className="lg:col-span-8">
            <div className="rounded-lg border border-background-200 bg-background-100/60 p-5 md:p-8">
              <h2 className="font-heading text-xl font-bold text-foreground-950">留言给我们</h2>
              <p className="mt-2 text-sm text-foreground-600">
                带 <span className="text-primary-600">*</span> 的为必填项。
              </p>

              <form
                data-readdy-form
                id="guatian-contact-form"
                onSubmit={handleSubmit}
                className="mt-6 flex flex-col gap-5"
              >
                <div className="flex flex-col gap-2">
                  <label htmlFor="contact-name" className="text-sm font-medium text-foreground-800">
                    怎么称呼你 <span className="text-primary-600">*</span>
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    placeholder="你的昵称或称呼"
                    className="rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="contact-email" className="text-sm font-medium text-foreground-800">
                    邮箱 <span className="text-primary-600">*</span>
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    placeholder="方便我们回复你"
                    className="rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="contact-type" className="text-sm font-medium text-foreground-800">
                    留言类型
                  </label>
                  <select
                    id="contact-type"
                    name="type"
                    defaultValue="纠错与补充"
                    className="rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
                  >
                    <option value="纠错与补充">纠错与补充</option>
                    <option value="线索投稿">线索投稿</option>
                    <option value="内容合作">内容合作</option>
                    <option value="其他">其他</option>
                  </select>
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="contact-message" className="text-sm font-medium text-foreground-800">
                    想说的话 <span className="text-primary-600">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="请尽量写清是哪篇文章、哪个细节，方便我们核实（500 字以内）"
                    className="resize-none rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  ></textarea>
                </div>

                <input
                  className="field-guard-note"
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"}></i>
                    {status === "submitting" ? "提交中…" : "发送留言"}
                  </button>
                  <p className="text-xs text-foreground-500">
                    我们只把邮箱用于回复，不会对外公开。
                  </p>
                </div>

                {status === "success" && (
                  <div className="flex items-start gap-2 rounded-md border border-accent-300 bg-accent-50 px-4 py-3 text-sm text-accent-900">
                    <i className="ri-checkbox-circle-line mt-0.5"></i>
                    <span>留言已收到，感谢你的反馈，我们会在工作日尽快回复你。</span>
                  </div>
                )}
                {status === "error" && (
                  <div className="flex items-start gap-2 rounded-md border border-primary-300 bg-primary-50 px-4 py-3 text-sm text-primary-900">
                    <i className="ri-error-warning-line mt-0.5"></i>
                    <span>{formError || "提交失败，请稍后再试。"}</span>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}